import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { reviews } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9qr62ue3oq4jqumtpe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请稍后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '害羞草指南',
    url: 'https://www.mimosa-guide.com/',
    description: '专注于害羞草（含羞草）种植养护知识的中文科普平台，提供从种植到养护的全方位指南。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@mimosa-guide.com',
      availableLanguage: 'Chinese',
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="害羞草指南 —— 最全面的中文害羞草知识平台。了解我们的品牌故事、口碑评价，以及联系方式。致力于为植物爱好者提供专业、易懂的害羞草种植养护知识。"
        canonicalUrl="https://www.mimosa-guide.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于我们
                </h1>
                <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  害羞草指南是一个专注于含羞草知识分享的中文科普平台。
                  我们致力于让更多人了解这株神奇植物，从科学原理到种植实操，
                  提供最全面、最易懂的内容。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                {[
                  { icon: 'ri-lightbulb-line', title: '我们的使命', desc: '将复杂的植物学知识转化为通俗易懂的内容，让每个人都能轻松了解和养护害羞草，感受自然的奇妙。' },
                  { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为中文世界最权威、最全面的害羞草知识平台，激发更多人对植物世界的探索热情。' },
                  { icon: 'ri-heart-line', title: '我们的价值观', desc: '科学严谨、内容真诚、持续学习。每一篇文章都经过仔细研究和验证，确保信息的准确性和实用性。' },
                ].map((item, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                    <span className="w-12 h-12 flex items-center justify-center text-2xl text-primary-500 mx-auto mb-4">
                      <i className={item.icon}></i>
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mb-14">
                <h2 className="font-heading text-2xl font-semibold text-foreground-900 text-center mb-8">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {reviews.map((review, i) => (
                    <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: review.rating }).map((_, j) => (
                          <span key={j} className="w-4 h-4 flex items-center justify-center text-accent-500 text-sm">
                            <i className="ri-star-fill"></i>
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        "{review.content}"
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-medium text-primary-700">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                          <p className="text-xs text-foreground-400">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="max-w-xl mx-auto">
                <h2 className="font-heading text-2xl font-semibold text-foreground-900 text-center mb-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 text-center mb-8">
                  有任何问题或建议？欢迎给我们留言
                </p>

                {formStatus === 'success' ? (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                    <span className="w-12 h-12 flex items-center justify-center text-3xl text-primary-500 mx-auto mb-3">
                      <i className="ri-check-line"></i>
                    </span>
                    <p className="text-foreground-800 font-medium">感谢您的留言！</p>
                    <p className="text-sm text-foreground-500 mt-1">我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                    {formStatus === 'error' && (
                      <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 text-sm text-accent-700">
                        {formError}
                      </div>
                    )}
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}